import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';

const PALETTE = ['#6366f1', '#0ea5e9', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#64748b', '#ec4899'];

const iso = (d) => d.toISOString().slice(0, 10);
const today = new Date();
const monthAgo = new Date(Date.now() - 30 * 24 * 3600 * 1000);

export default function Reports() {
  const [from, setFrom] = useState(iso(monthAgo));
  const [to, setTo] = useState(iso(today));
  const [companyId, setCompanyId] = useState('');
  const [companies, setCompanies] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { from, to };
      if (companyId) params.companyId = companyId;
      const { data } = await api.get('/reports', { params });
      setData(data);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  }, [from, to, companyId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/companies').then(r => setCompanies(r.data)).catch(() => {}); }, []);

  const slaTotal = (data?.slaMet || 0) + (data?.slaBreached || 0);
  const slaPct = slaTotal ? Math.round((data.slaMet / slaTotal) * 100) : null;
  const avgH = data?.avgResolutionHours || 0;
  const avgLabel = avgH >= 48 ? `${(avgH / 24).toFixed(1)} d` : `${avgH} h`;

  const cards = data ? [
    { label: 'Total', value: data.total, color: '#7c3aed' },
    { label: 'Resolved', value: data.resolved, color: '#059669' },
    { label: 'Open', value: data.open, color: '#2563eb' },
    { label: 'Overdue', value: data.overdue, color: '#dc2626' },
    { label: 'Avg Resolution', value: avgLabel, color: '#0891b2' },
    { label: 'SLA Compliance', value: slaPct === null ? '—' : `${slaPct}%`, color: slaPct !== null && slaPct < 80 ? '#dc2626' : '#059669' },
  ] : [];

  const exportCsv = () => {
    if (!data?.rows?.length) return;
    const cols = ['ticketNumber', 'subject', 'status', 'priority', 'company', 'assignedTo', 'createdAt', 'resolvedAt', 'dueAt', 'resolutionHours'];
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = [cols.join(',')].concat(data.rows.map(r => cols.map(c => esc(r[c])).join(',')));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `support-report_${from}_to_${to}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#0f172a' }}>Reports</h1>
          <p style={{ color: '#64748b' }}>Ticket analytics for your visible tickets</p>
        </div>
        <button className="btn-primary" onClick={exportCsv} disabled={!data?.rows?.length}>⬇ Export CSV</button>
      </div>

      <div className="card" style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 20 }}>
        <div><label style={lbl}>From</label><input type="date" value={from} onChange={e => setFrom(e.target.value)} /></div>
        <div><label style={lbl}>To</label><input type="date" value={to} onChange={e => setTo(e.target.value)} /></div>
        <div style={{ minWidth: 200 }}>
          <label style={lbl}>Company</label>
          <select value={companyId} onChange={e => setCompanyId(e.target.value)}>
            <option value="">All companies</option>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 16, marginBottom: 24 }}>
        {cards.map(c => (
          <div key={c.label} className="card">
            <div style={{ fontSize: 26, fontWeight: 700, color: c.color }}>{loading ? '—' : c.value}</div>
            <div style={{ fontSize: 13, color: '#64748b' }}>{c.label}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginBottom: 24 }}>
        <BarCard title="By Status" map={data?.byStatus} loading={loading} />
        <BarCard title="By Priority" map={data?.byPriority} loading={loading} />
        <BarCard title="By Category" map={data?.byCategory} loading={loading} />
        <BarCard title="By Company" map={data?.byCompany} loading={loading} />
        <BarCard title="By Assignee" map={data?.byAssignee} loading={loading} />
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--gray-200)', fontWeight: 600 }}>
          Tickets {data?.rows ? `(${data.rows.length})` : ''}
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead><tr><th>Ticket</th><th>Subject</th><th>Company</th><th>Status</th><th>Priority</th><th>Assignee</th><th>Resolution (h)</th></tr></thead>
            <tbody>
              {loading && <tr><td colSpan={7} style={{ color: '#94a3b8' }}>Loading…</td></tr>}
              {!loading && (!data?.rows || data.rows.length === 0) && <tr><td colSpan={7} style={{ color: '#94a3b8' }}>No tickets in range.</td></tr>}
              {!loading && data?.rows?.map((r, i) => (
                <tr key={i}>
                  <td style={{ fontWeight: 600 }}>{r.ticketNumber}</td>
                  <td>{r.subject}</td>
                  <td>{r.company}</td>
                  <td>{r.status}</td>
                  <td>{r.priority}</td>
                  <td>{r.assignedTo || <span style={{ color: '#94a3b8' }}>Unassigned</span>}</td>
                  <td>{r.resolutionHours ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function BarCard({ title, map, loading }) {
  const rows = map ? Object.entries(map) : [];
  const max = Math.max(1, ...rows.map(([, v]) => v));
  return (
    <div className="card">
      <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 14 }}>{title}</h2>
      {loading ? <div style={{ color: '#94a3b8', fontSize: 13 }}>Loading…</div>
        : rows.length === 0 ? <div style={{ color: '#94a3b8', fontSize: 13 }}>No data.</div>
          : rows.map(([label, count], i) => (
            <div key={label} style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                <span style={{ color: '#334155' }}>{label}</span>
                <span style={{ fontWeight: 600 }}>{count}</span>
              </div>
              <div style={{ height: 8, background: 'var(--gray-100)', borderRadius: 999, overflow: 'hidden' }}>
                <div style={{ width: `${(count / max) * 100}%`, height: '100%', background: PALETTE[i % PALETTE.length], borderRadius: 999 }} />
              </div>
            </div>
          ))}
    </div>
  );
}

const lbl = { fontSize: 12, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 4 };
